import React, { useState, useEffect, useRef } from 'react';
import { Copy } from 'lucide-react';
import Konva from "konva";
import "../../assets/css/CreateYourOwn/CommonCreate.css"
import "../../assets/css/CreateYourOwn/HashBuilder.css"
import "../../assets/css/colors.css"
import { useLanguage } from 'context/LanguageProvider';
import { useDarkMode } from 'context/DarkModeProvider';
import CodeHighlighter from 'components/CodeHighlighter';

const HashTableBuilder = () => {
    const [tableSize, setTableSize]               = useState(7);
    const [hashFunction, setHashFunction]         = useState('modulo');
    const [inputValue, setInputValue]             = useState('');
    const [hashTable, setHashTable]               = useState({});
    const [selectedLanguage, setSelectedLanguage] = useState('javascript');
    const [stage, setStage]                       = useState(null);
    const [layer, setLayer]                       = useState(null);
    const [animationLayer, setAnimationLayer]     = useState(null);
    const canvasRef                               = useRef(null);
    const [copied, setCopied]                     = useState(false);
    const [scaleFactor, setScaleFactor]           = useState(1);
    const [actionLog, setActionLog]               = useState([]);

    const { darkMode }    = useDarkMode();
    const { selLanguage } = useLanguage();

    // ── Scale factor ──────────────────────────────────────────────────────────
    useEffect(() => {
        const handleResize = () => {
            const w = window.innerWidth;
            setScaleFactor(w <= 480 ? 0.6 : w <= 768 ? 0.75 : 1);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (stage) {
            stage.height(600 * scaleFactor);
            layer?.draw();
            animationLayer?.draw();
        }
    }, [scaleFactor, stage, layer, animationLayer]);

    // ── Init Konva ────────────────────────────────────────────────────────────
    useEffect(() => {
        if (canvasRef.current && !stage) {
            const newStage     = new Konva.Stage({ container: canvasRef.current, width: 700, height: 600 });
            const newLayer     = new window.Konva.Layer();
            const newAnimLayer = new window.Konva.Layer();
            newStage.add(newLayer);
            newStage.add(newAnimLayer);
            setStage(newStage);
            setLayer(newLayer);
            setAnimationLayer(newAnimLayer);
        }
    }, [stage]);

    // ── Reset on size change ──────────────────────────────────────────────────
    useEffect(() => {
        const reset = {};
        for (let i = 0; i < tableSize; i++) reset[i] = [];
        setHashTable(reset);
        setInputValue('');
        setActionLog(prev => [...prev, { type: 'reset', tableSize }]);
        if (animationLayer) { animationLayer.destroyChildren(); animationLayer.draw(); }
    }, [tableSize]);

    // ── Hash functions ────────────────────────────────────────────────────────
    const hashFunctions = {
        modulo: (key) => {
            if (!isNaN(key)) return Number(key) % tableSize;
            let h = 0;
            for (let i = 0; i < key.length; i++) h += key.charCodeAt(i);
            return h % tableSize;
        },
        djb2: (key) => {
            let h = 5381;
            for (let i = 0; i < key.length; i++) h = ((h << 5) + h) + key.charCodeAt(i);
            return Math.abs(h) % tableSize;
        },
        fnv: (key) => {
            let h = 2166136261;
            for (let i = 0; i < key.length; i++) { h ^= key.charCodeAt(i); h = (h * 16777619) % Math.pow(2, 32); }
            return Math.abs(h) % tableSize;
        }
    };

    // ── Insert (chaining only) ────────────────────────────────────────────────
    const insertItem = () => {
        if (!inputValue.trim()) { alert("Πρέπει να γράψεις ένα στοιχείο που θες να εισάγεις"); return; }
        const hashValue    = hashFunctions[hashFunction](inputValue);
        const newHashTable = { ...hashTable };
        if (!newHashTable[hashValue]) newHashTable[hashValue] = [];
        newHashTable[hashValue].push(inputValue);

        animateInsertion(inputValue, hashValue);
        setActionLog(prev => [...prev, { type: 'insert', value: inputValue, index: hashValue }]);
        setTimeout(() => { setHashTable(newHashTable); setInputValue(''); }, 1000);
    };

    // ── Animation ─────────────────────────────────────────────────────────────
    const animateInsertion = (value, targetIndex) => {
        if (!animationLayer) return;
        animationLayer.destroyChildren();
        const startY  = 20 * scaleFactor;
        const cellH   = 60 * scaleFactor;
        const targetY = 80 * scaleFactor + targetIndex * (cellH + 15 * scaleFactor);

        const animRect = new window.Konva.Rect({ x: 350 * scaleFactor, y: startY, width: 80 * scaleFactor, height: 40 * scaleFactor, fill: '#ff6b6b', stroke: '#e55151', strokeWidth: 2 * scaleFactor, cornerRadius: 8 * scaleFactor, shadowColor: '#ff6b6b', shadowBlur: 10 * scaleFactor, shadowOpacity: 0.6 });
        const animText = new window.Konva.Text({ x: 355 * scaleFactor, y: startY + 12 * scaleFactor, text: value, fontSize: 14 * scaleFactor, fill: 'white', fontFamily: 'Inter, Arial', fontStyle: 'bold', width: 70 * scaleFactor, align: 'center' });

        animationLayer.add(animRect);
        animationLayer.add(animText);
        new window.Konva.Tween({ node: animRect, duration: 1, y: targetY, easing: window.Konva.Easings.EaseInOut, onFinish: () => { animationLayer.destroyChildren(); animationLayer.draw(); } }).play();
        new window.Konva.Tween({ node: animText, duration: 1, y: targetY + 12 * scaleFactor, easing: window.Konva.Easings.EaseInOut }).play();
        animationLayer.draw();
    };

    // ── Clear ─────────────────────────────────────────────────────────────────
    const clearTable = () => {
        const reset = {};
        for (let i = 0; i < tableSize; i++) reset[i] = [];
        setHashTable(reset);
        setActionLog(prev => [...prev, { type: 'clear' }]);
    };

    // ── Draw (chaining only) ──────────────────────────────────────────────────
    useEffect(() => {
        if (!layer || !stage) return;
        layer.destroyChildren();
        const cellW  = 100 * scaleFactor;
        const cellH  = 60  * scaleFactor;
        const startX = 80  * scaleFactor;
        const startY = 50  * scaleFactor;

        for (let i = 0; i < tableSize; i++) {
            const y = startY + i * (cellH + 15 * scaleFactor);

            // Bucket rect
            layer.add(new window.Konva.Rect({ x: startX, y, width: cellW, height: cellH, fill: 'rgba(102,126,234,0.15)', stroke: '#667eea', strokeWidth: 2 * scaleFactor, cornerRadius: 12 * scaleFactor, shadowColor: '#667eea', shadowBlur: 10 * scaleFactor, shadowOpacity: 0.2 }));
            // Index label
            layer.add(new window.Konva.Text({ x: startX + 5 * scaleFactor, y: y + 22 * scaleFactor, text: i.toString(), fontSize: 16 * scaleFactor, fill: '#667eea', fontFamily: 'Inter, Arial', fontStyle: 'bold', width: cellW - 10 * scaleFactor, align: 'center' }));

            const items = Array.isArray(hashTable[i]) ? hashTable[i] : [];
            items.forEach((item, ci) => {
                const chainX = startX + cellW + 30 * scaleFactor + ci * 90 * scaleFactor;
                layer.add(new window.Konva.Rect({ x: chainX, y: y + 5 * scaleFactor, width: 75 * scaleFactor, height: cellH - 10 * scaleFactor, fill: '#4facfe', stroke: '#00d2ff', strokeWidth: 2 * scaleFactor, cornerRadius: 10 * scaleFactor, shadowColor: '#4facfe', shadowBlur: 12 * scaleFactor, shadowOpacity: 0.4 }));
                layer.add(new window.Konva.Text({ x: chainX + 5 * scaleFactor, y: y + cellH / 2 - 8 * scaleFactor, text: item.toString(), fontSize: 13 * scaleFactor, fill: 'white', fontFamily: 'Inter, Arial', fontStyle: 'bold', width: 65 * scaleFactor, align: 'center' }));
                layer.add(new window.Konva.Arrow({ points: ci === 0 ? [startX + cellW + 5 * scaleFactor, y + cellH / 2, chainX - 5 * scaleFactor, y + cellH / 2] : [chainX - 15 * scaleFactor, y + cellH / 2, chainX - 5 * scaleFactor, y + cellH / 2], pointerLength: ci === 0 ? 12 * scaleFactor : 8 * scaleFactor, pointerWidth: ci === 0 ? 8 * scaleFactor : 6 * scaleFactor, fill: ci === 0 ? '#667eea' : '#4facfe', stroke: ci === 0 ? '#667eea' : '#4facfe', strokeWidth: ci === 0 ? 3 * scaleFactor : 2 * scaleFactor }));
            });
        }
        layer.draw();
    }, [hashTable, tableSize, layer, stage, scaleFactor]);

    // ── Code generation ───────────────────────────────────────────────────────
    const generateCode = () => {
        const renderActions = (lang) => actionLog.map(action => {
            if (action.type === 'insert') {
                if (lang === 'js')   return `    myHashTable.insert("${action.value}", "${action.value}");`;
                if (lang === 'py')   return `    my_hash_table.insert("${action.value}", "${action.value}")`;
                if (lang === 'java') return `        myHashTable.insert("${action.value}", "${action.value}");`;
                if (lang === 'cpp')  return `    myHashTable.insert("${action.value}", "${action.value}");`;
            }
            if (action.type === 'clear') {
                if (lang === 'js')   return `    // Hash table cleared at this point`;
                if (lang === 'py')   return `    # Hash table cleared at this point`;
                if (lang === 'java') return `        // Hash table cleared at this point`;
                if (lang === 'cpp')  return `    // Hash table cleared at this point`;
            }
            if (action.type === 'reset') {
                if (lang === 'js')   return `    // Settings changed: size=${action.tableSize}`;
                if (lang === 'py')   return `    # Settings changed: size=${action.tableSize}`;
                if (lang === 'java') return `        // Settings changed: size=${action.tableSize}`;
                if (lang === 'cpp')  return `    // Settings changed: size=${action.tableSize}`;
            }
            return '';
        }).filter(Boolean).join('\n');

        const hashImpl = {
            js:   `let h = 0;\n        for (let i = 0; i < key.length; i++) h += key.charCodeAt(i);\n        return h % this.size;`,
            py:   `h = sum(ord(c) for c in str(key))\n        return h % self.size`,
            java: `int h = 0;\n        for (char c : key.toCharArray()) h += c;\n        return h % size;`,
            cpp:  `int h = 0;\n        for (char c : key) h += c;\n        return h % size;`,
        };

        const codes = {
            javascript: `// Hash Table with Separate Chaining in JavaScript

class HashTable {
    constructor(size = ${tableSize}) {
        this.size = size;
        this.table = new Array(size).fill(null).map(() => []);
    }

    hash(key) {
        ${hashImpl.js}
    }

    insert(key, value) {
        const index = this.hash(key);
        this.table[index].push(value);
    }

    search(key) {
        const index = this.hash(key);
        return this.table[index];
    }

    remove(key, value) {
        const index = this.hash(key);
        this.table[index] = this.table[index].filter(v => v !== value);
    }
}

// Main
function main() {
    const myHashTable = new HashTable(${tableSize});
${renderActions('js')}
    console.log(myHashTable.table);
}

main();`,

            python: `# Hash Table with Separate Chaining in Python

class HashTable:
    def __init__(self, size=${tableSize}):
        self.size = size
        self.table = [[] for _ in range(size)]

    def hash(self, key):
        ${hashImpl.py}

    def insert(self, key, value):
        index = self.hash(key)
        self.table[index].append(value)

    def search(self, key):
        index = self.hash(key)
        return self.table[index]

    def remove(self, key, value):
        index = self.hash(key)
        self.table[index] = [v for v in self.table[index] if v != value]

# Main
if __name__ == "__main__":
    my_hash_table = HashTable(${tableSize})
${renderActions('py')}
    print(my_hash_table.table)`,

            java: `// Hash Table with Separate Chaining in Java
import java.util.*;

class HashTable {
    private int size;
    private List<List<String>> table;

    public HashTable(int size) {
        this.size = size;
        this.table = new ArrayList<>(size);
        for (int i = 0; i < size; i++) table.add(new ArrayList<>());
    }

    private int hash(String key) {
        ${hashImpl.java}
    }

    public void insert(String key, String value) {
        int index = hash(key);
        table.get(index).add(value);
    }

    public List<String> search(String key) {
        return table.get(hash(key));
    }

    public void remove(String key, String value) {
        table.get(hash(key)).remove(value);
    }
}

public class Main {
    public static void main(String[] args) {
        HashTable myHashTable = new HashTable(${tableSize});
${renderActions('java')}
        System.out.println("Hash table created with size ${tableSize}");
    }
}`,

            cpp: `// Hash Table with Separate Chaining in C++
#include <iostream>
#include <vector>
#include <string>
#include <algorithm>
using namespace std;

class HashTable {
private:
    int size;
    vector<vector<string>> table;

    int hash(const string& key) {
        ${hashImpl.cpp}
    }

public:
    HashTable(int s = ${tableSize}) : size(s), table(s) {}

    void insert(const string& key, const string& value) {
        table[hash(key)].push_back(value);
    }

    vector<string> search(const string& key) {
        return table[hash(key)];
    }

    void remove(const string& key, const string& value) {
        auto& chain = table[hash(key)];
        chain.erase(remove(chain.begin(), chain.end(), value), chain.end());
    }
};

int main() {
    HashTable myHashTable(${tableSize});
${renderActions('cpp')}
    cout << "Hash table created with size ${tableSize}" << endl;
    return 0;
}`,
        };

        return codes[selectedLanguage] || '';
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const btnBase = {
        padding: '16px 24px', borderRadius: '16px', border: '2px solid #7a8effff',
        fontSize: '1rem', fontWeight: '600', cursor: 'pointer',
        transition: 'all 0.2s ease', fontFamily: 'Poppins, sans-serif',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        background: darkMode ? "#1e293b" : "var(--customBuildButton)",
        color: "#7a8effff",
    };

    return (
        <div className='commonContainerCreate' style={{ background: darkMode ? '#0f172a' : 'var(--lightModeBack)' }}>
            <div className='commonVisual' style={{
                background: darkMode ? '#1E293B' : 'var(--customBuildBack)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderColor: darkMode ? 'rgba(122,142,255,0.3)' : 'rgba(0,0,0,0.2)',
            }}>
                <h1 className='commonCreateTitle'>
                    {selLanguage ? "Δημιουργία Πίνακα Κατακερματισμού" : "HashTable Builder"}
                </h1>

                {/* Controls */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ color: darkMode ? 'white' : 'black' }} className='HashLabelCreate'>
                            {selLanguage ? "Μέγεθος Πίνακα" : "Table Size"}
                        </label>
                        <select className='HashSelectCreate' value={tableSize} onChange={(e) => setTableSize(parseInt(e.target.value))}
                            onFocus={(e) => e.target.style.borderColor = '#667eea'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}>
                            {[...Array(7)].map((_, i) => <option key={i + 1} value={i + 1}>{i + 1}</option>)}
                        </select>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <label style={{ color: darkMode ? 'white' : 'black' }} className='HashLabelCreate'>
                            Hash Function
                        </label>
                        <p style={{ color: darkMode ? "white" : "black" }}>
                            Simple Modulo = number modulo table size
                        </p>
                    </div>
                </div>

                {/* Input */}
                <div className='HashInputContainerCreate'>
                    <input
                        className="custom-input HashInputCreate"
                        type="text"
                        value={inputValue}
                        onChange={(e) => { if (/^\d*$/.test(e.target.value)) setInputValue(e.target.value); }}
                        onKeyDown={(e) => e.key === 'Enter' && insertItem()}
                        placeholder={selLanguage ? "Γράψε τιμή για εισαγωγή..." : "Enter value to insert..."}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                    />
                    <style>{`.custom-input::placeholder { color: #363a42ff; opacity: 1; }`}</style>
                    <div className="HashButtonGroupCreate">
                        <button onClick={insertItem} style={btnBase}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                            {selLanguage ? "Εισαγωγή" : "Insert"}
                        </button>
                        <button onClick={clearTable} style={btnBase}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                            {selLanguage ? "Καθαρισμός" : "Restart"}
                        </button>
                    </div>
                </div>

                {/* Canvas */}
                <div ref={canvasRef} style={{
                    border: !darkMode ? '1px solid #0b0b0cff' : 'none',
                    borderRadius: '20px',
                    background: "linear-gradient(135deg, rgb(245,247,250) 0%, rgb(195,207,226) 100%)",
                    boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.06)'
                }} />

                <br /><br />

                {/* Language Label */}
                <label className='codeLabelCreate' style={{ color: darkMode ? "#fff" : "#040404ff" }}>
                    {selLanguage ? "Γλώσσα Προγραμματισμού" : "Programming Language"}
                </label>

                {/* Language Selector */}
                <div style={{ marginBottom: '20px' }}>
                    <select className='codeSelectorCreate' value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}
                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                        onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}>
                        <option value="javascript">JavaScript</option>
                        <option value="python">Python</option>
                        <option value="cpp">C++</option>
                        <option value="java">Java</option>
                    </select>
                </div>

                {/* Code Display */}
                <div style={{
                    background: 'linear-gradient(145deg, #0f172a, #1e293b)',
                    borderRadius: '8px',
                    border: '1px solid #334155',
                    overflow: 'auto',
                    maxHeight: '300px',
                    fontSize: '0.75rem',
                }}>
                    <CodeHighlighter code={generateCode()} language={selectedLanguage} />
                </div>

                {/* Copy Button */}
                <button
                    onClick={() => { navigator.clipboard.writeText(generateCode()); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    className='commonButtonCreate'
                    style={{
                        width: '100%', marginTop: '20px',
                        background: copied
                            ? 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                            : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        transform: copied ? 'translateY(-4px)' : 'translateY(0)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => !copied && (e.currentTarget.style.transform = 'translateY(-2px)')}
                    onMouseOut={(e) => !copied && (e.currentTarget.style.transform = 'translateY(0)')}
                >
                    <Copy size={16} style={{ marginRight: '8px' }} />
                    {copied
                        ? (selLanguage ? ' Αντιγράφηκε!' : ' Copied')
                        : (selLanguage ? ' Αντιγραφή' : ' Copy')}
                </button>
            </div>
        </div>
    );
};

export default HashTableBuilder;